import { ActionIcon, Alert, AppShell, Avatar, Badge, Box, Burger, Group, Menu, NavLink, ScrollArea, Select, Stack, Text, Tooltip, useMantineColorScheme, useMantineTheme, type MantineColorScheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { OrgStatus } from '@motionsafe/ms-console-api/common/v1/common_pb.js';
import { IconAlertTriangle, IconBell, IconBellFilled, IconBuildingCommunity, IconChevronRight, IconDatabase, IconDeviceTablet, IconKey, IconLayoutDashboard, IconListDetails, IconLogout, IconMail, IconScale, IconSelector, IconShare, IconShieldCheck, IconShieldCog, IconShieldLock, IconUsers } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, Outlet, redirect, useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { NotConfiguredError, signingOut } from '../api/client';
import { androidAutoQuery, deviceCountsQuery, meQuery, notificationsQuery, orgQuery, staffRolesQuery } from '../api/hooks';
import { useOrgSearch } from '../components/org-search';
import { devicesIn } from '../lib/counts';
import { accessFor, isStaff, joinRoles, sessionStaffRoles, type Access } from '../lib/roles';
import { ICON, ICON_INLINE, menuFromAbove } from '../theme';
import { ExpiryMark, expiryFlag } from '../components/android-auto-expiry';
import { ChangeAuthenticatorModal } from '../components/change-authenticator';
import { ChangeEmailModal } from '../components/change-email';
import { ChangePasswordModal } from '../components/change-password';
import { Wordmark } from '../components/logo';
import { PageLoadBar } from '../components/top-load-bar';

// Every signed-in page. Staff browse any organization through ?org=<id>;
// org admins are pinned to their own, and the parameter is ignored for them.
export interface AppSearch { org?: string | undefined }

export const Route = createFileRoute('/_app')({
  validateSearch: (s: Record<string, unknown>): AppSearch => (typeof s['org'] === 'string' ? { org: s['org'] } : {}),
  beforeLoad: async ({ context, location }) => {
    // A service without its secrets sends everyone to the root, where the
    // sign-in page explains what is missing.
    const me = await context.queryClient.fetchQuery(meQuery).catch((e: unknown) => {
      if (e instanceof NotConfiguredError) return null;
      throw e;
    });
    // Sign-in brings the person back to the page they asked for.
    if (!me) throw redirect({ to: '/', search: { next: `${location.pathname}${location.searchStr}` } });
    // Staff who are not admins lock controls by the staff roles table, so it is there before the first page draws.
    if (me.consoleRole === 'ms_staff') await context.queryClient.ensureQueryData(staffRolesQuery).catch(() => undefined);
    return { me };
  },
  component: Shell,
});

// What MotionSafe staff have beyond an organization's own pages: the list
// of organizations, the whole fleet, everything recorded or noticed across
// all of them, every share of a collection, what devices download, MotionSafe's legal documents, and what
// each staff role can change. They are the first section of a staff
// member's sidebar; the organization in view follows under its own name.
export const STAFF_PAGES = ['/orgs', '/all-devices', '/trail', '/all-sharing', '/inbox', '/device-content', '/legal-documents', '/staff-roles'];

/** The staff page a path is, or is under (a legal document is under its list). */
function staffPageOf(path: string): string | undefined {
  return STAFF_PAGES.find((p) => path === p || path.startsWith(`${p}/`));
}

/** Whether the page in view is one of the staff's across-organization pages. */
export function useTopLevel(): boolean {
  const { me } = Route.useRouteContext();
  const path = useLocation({ select: (l) => l.pathname });
  return isStaff(me) && staffPageOf(path) !== undefined;
}

/** The organization in view: for staff the one they chose, their own (MotionSafe) until they choose; for anyone else, theirs. */
export function useOrgId(): string {
  const { me } = Route.useRouteContext();
  const { org } = useSearch({ from: '/_app' });
  return isStaff(me) && org ? org : me.orgId;
}

/**
 * What the person signed in may change, call by call. Staff who are not
 * admins see what their roles do not allow locked, with a caption naming
 * the role it needs (lib/roles.ts).
 */
export function useAccess(): Access {
  const { me } = Route.useRouteContext();
  const table = useQuery({ ...staffRolesQuery, enabled: me.consoleRole === 'ms_staff' }).data;
  return accessFor(me, table);
}

// Appearance follows the system unless the person picks one; Mantine keeps
// the choice per browser.
const SCHEMES: { value: MantineColorScheme; label: string }[] = [
  { value: 'auto', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

// A section link is current on its page and anything under it, whatever the
// query says: the router marks it aria-current="page" and NavLink styles
// that, so there is no second active rule to keep in step.
const SECTION_MATCH = { includeSearch: false } as const;

// The sections, grouped by what an administrator comes to do. Staff get the
// pages across every organization first; everything else is about the
// organization in view, named over its sections so the two never blur.
type Item = { to: string; label: string; icon: ReactNode; /** Shown against the right edge when it is not zero. */ count?: number; /** Shown against the right edge instead of a count: something on the page needs attention. */ mark?: ReactNode };

function Shell() {
  const { me } = Route.useRouteContext();
  const orgId = useOrgId();
  const org = useQuery(orgQuery(orgId)).data;
  const staff = isStaff(me);
  // Staff see every organization's inbox, so their count is across all of them.
  const unread = useQuery(notificationsQuery(staff ? '' : me.orgId, true)).data?.unread ?? 0;
  const unreadLabel = staff ? `${unread} unread across all organizations` : `${unread} unread`;
  // There is one view: an organization's. Staff are of MotionSafe, which
  // is an organization like any other, and what they have beyond it is on
  // the staff menu in the top bar. Every link carries the organization in
  // view, so a link to a page keeps the organization it was about.
  const top = useTopLevel();
  const search: AppSearch = staff ? { org: orgId } : {};
  const [navOpen, { toggle, close }] = useDisclosure(false);
  const { motion } = useMantineTheme().other;

  // How many devices are waiting for an organization: said in the menu, beside the page that lists them.
  const unassigned = devicesIn(useQuery({ ...deviceCountsQuery('', true), enabled: staff }).data ?? [], (g) => g.unassigned && !g.revoked);
  // The Android Auto certificate running out: marked beside the page where it is replaced.
  const aaFlag = expiryFlag(useQuery({ ...androidAutoQuery(), enabled: staff }).data?.current?.presented);
  const staffPages: Item[] = [
    { to: '/orgs', label: 'All organizations', icon: <IconBuildingCommunity {...ICON} /> },
    { to: '/all-devices', label: 'All devices', icon: <IconDeviceTablet {...ICON} />, count: unassigned },
    { to: '/trail', label: 'All audit events', icon: <IconListDetails {...ICON} /> },
    { to: '/all-sharing', label: 'All shares', icon: <IconShare {...ICON} /> },
    { to: '/inbox', label: 'All notifications', icon: <IconBell {...ICON} /> },
    { to: '/device-content', label: 'Device content', icon: <IconDatabase {...ICON} />, ...(aaFlag ? { mark: <ExpiryMark flag={aaFlag} /> } : {}) },
    { to: '/legal-documents', label: 'Legal documents', icon: <IconScale {...ICON} /> },
    { to: '/staff-roles', label: 'Staff roles', icon: <IconShieldCog {...ICON} /> },
  ];
  const groups: { title?: string; items: Item[] }[] = [
    ...(staff ? [{ title: 'Across organizations', items: staffPages }] : []),
    { ...(staff ? { title: org?.name ?? '\u00a0' } : {}), items: [
      { to: '/overview', label: 'Overview', icon: <IconLayoutDashboard {...ICON} /> },
      { to: '/people', label: 'People', icon: <IconUsers {...ICON} /> },
      { to: '/devices', label: 'Devices', icon: <IconDeviceTablet {...ICON} /> },
    ] },
    { title: 'Security', items: [
      // The organization key is MotionSafe's to see and manage.
      ...(staff ? [{ to: '/keys', label: 'Keys', icon: <IconKey {...ICON} /> }] : []),
      { to: '/policy', label: 'Policy', icon: <IconShieldCheck {...ICON} /> },
    ] },
    { title: 'Record', items: [
      { to: '/audit', label: 'Audit trail', icon: <IconListDetails {...ICON} /> },
      { to: '/sharing', label: 'Sharing audit', icon: <IconShare {...ICON} /> },
      { to: '/notifications', label: 'Notifications', icon: <IconBell {...ICON} /> },
    ] },
  ];

  // The section in view, for the crumb in the top bar.
  const path = useLocation({ select: (l) => l.pathname });
  const section = groups.flatMap((g) => g.items).find((it) => it.to === path || (top && it.to === staffPageOf(path)))?.label ?? '';
  // Pages opened from the account menu are the person's own, not a section of the sidebar.
  const crumb = section || (path === '/your-legal-documents' ? 'Your legal documents' : '');
  const held = sessionStaffRoles(me);
  const role = !staff ? 'Organization administrator' : me.consoleRole === 'ms_superadmin' ? 'MotionSafe admin' : held.length ? `MotionSafe staff, ${joinRoles(held)}` : 'MotionSafe staff, read only';

  return (
    <AppShell
      layout="alt"
      header={{ height: 56 }}
      navbar={{ width: 248, breakpoint: 'sm', collapsed: { mobile: !navOpen } }}
      padding={0}
      transitionDuration={motion.surface}
      transitionTimingFunction="var(--ease-out)"
    >
      {/* The top bar: where you are on the left, the bell and the account on
          the right. It spans the content, so the sidebar's own letterhead
          sits beside it at the same height. */}
      <AppShell.Header px={{ base: 'md', sm: 'xl' }} style={{ background: 'var(--console-nav-bg)' }}>
        <Group h="100%" justify="space-between" wrap="nowrap" gap="md">
          <Group gap="xs" wrap="nowrap" miw={0}>
            <Burger hiddenFrom="sm" opened={navOpen} onClick={toggle} size="sm" aria-label={navOpen ? 'Close sections' : 'Open sections'} />
            {/* Where you are: the organization in view, then the page. On a staff page the first is every organization. */}
            <Text size="sm" fw={600} lineClamp={1} {...(top && crumb ? { visibleFrom: 'xs' } : {})}>{top ? 'Across organizations' : org?.name ?? '\u00a0'}</Text>
            {crumb && (
              <>
                <Box component="span" lh={0} style={{ flex: 'none' }} {...(top ? { visibleFrom: 'xs' } : {})}><IconChevronRight {...ICON_INLINE} style={{ color: 'var(--mantine-color-dimmed)' }} aria-hidden="true" /></Box>
                <Text size="sm" c="dimmed" style={{ flex: 'none' }}>{crumb}</Text>
              </>
            )}
          </Group>
          <Group gap="xs" wrap="nowrap">
            {/* Something unread turns the bell amber and fills it, so it is told by shape as well as color; how many is on the page it opens. */}
            <Tooltip label={unread ? unreadLabel : 'Nothing unread'} openDelay={400}>
              <ActionIcon variant="subtle" color={unread ? 'warning' : 'gray'} size="lg" aria-label={unread ? `Notifications, ${unreadLabel}` : 'Notifications'} renderRoot={(p) => (staff ? <Link {...p} to="/inbox" search={search} /> : <Link {...p} to="/notifications" search={search} />)}>
                {unread ? <IconBellFilled {...ICON} /> : <IconBell {...ICON} />}
              </ActionIcon>
            </Tooltip>
            <AccountMenu name={me.displayName} role={role} authenticator={!!me.authenticator} changeEmail={!!me.changeEmail} search={search} />
          </Group>
        </Group>
        <PageLoadBar />
      </AppShell.Header>

      <AppShell.Navbar aria-label="Sections" style={{ background: 'var(--console-nav-bg)' }}>
        {/* The letterhead row: the sidebar runs the full height of the window
            and this is its top, level with the top bar beside it and ruled
            off like it. On a phone the sidebar covers the page, so the row
            carries the control that closes it. */}
        <AppShell.Section px="md" h={56} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--mantine-color-default-border)' }}>
          <Wordmark size={18} />
          <Burger hiddenFrom="sm" opened onClick={close} size="sm" aria-label="Close sections" />
        </AppShell.Section>

        {/* Sections in groups: a small label above each group, rows of one
            height with the icon in its own column, the current one on the
            selection tint with its label a step bolder. */}
        <AppShell.Section grow component={ScrollArea} px="sm" pt="sm">
          {groups.map((g, i) => (
            <Box key={i} component="nav" aria-label={g.title} mb="xs">
              {g.title && <Text size="xs" c="dimmed" fw={600} px="sm" pt="lg" pb={6}>{g.title}</Text>}
              <Stack gap={2}>
                {g.items.map((it) => (
                  <NavLink key={it.to} renderRoot={(p) => <Link {...p} to={it.to} search={search} activeOptions={SECTION_MATCH} />} label={it.label} leftSection={it.icon} onClick={close} {...(it.count ? { rightSection: <Badge size="sm" color="warning" className="num" aria-label={`${it.count} waiting`}>{it.count}</Badge> } : it.mark ? { rightSection: it.mark } : {})} />
                ))}
              </Stack>
            </Box>
          ))}
        </AppShell.Section>

        {/* The organization in view, at the foot, where a workspace switcher
            lives in every console of this kind; the list of all of them is
            its first entry. Organization administrators see their own name. */}
        <AppShell.Section px="sm" py="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
          {staff ? <OrgSwitcher current={orgId} /> : (
            <Group gap="sm" px="sm" py={6} wrap="nowrap">
              <OrgMark name={org?.name ?? ''} />
              <Text size="sm" fw={600} lineClamp={1}>{org?.name ?? '\u00a0'}</Text>
            </Group>
          )}
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        {/* The page takes the full width beside the sidebar, inside its padding: tables use the room a wide screen has. */}
        <Box px={{ base: 'md', sm: 'xl' }} pt={{ base: 'lg', sm: 40 }} pb="xl">
          {org?.status === OrgStatus.SUSPENDED && (
            <Alert color="danger" mb="xl" role="status" icon={<IconAlertTriangle {...ICON} />} title="Suspended by MotionSafe">
              Operators cannot sign in and their sessions end at the next refresh. Everything here is kept. Contact your account manager.
            </Alert>
          )}
          <Outlet />
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}

/** The organization's initial in a small tile, beside its name. */
function OrgMark({ name }: { name: string }) {
  return (
    <Box w={24} h={24} style={{ flex: 'none', borderRadius: 6, display: 'grid', placeItems: 'center', background: 'var(--mantine-primary-color-light)', color: 'var(--mantine-primary-color-light-color)', fontSize: 12, fontWeight: 700 }} aria-hidden="true">
      {(name.trim().charAt(0) || 'M').toUpperCase()}
    </Box>
  );
}

// Staff change the organization in view here; the choice travels in the URL
// so a link to a page keeps the organization it was about. The list of
// every organization is first in the sidebar's Across organizations.
function OrgSwitcher({ current }: { current: string }) {
  const orgs = useOrgSearch({ value: current });
  const navigate = useNavigate();
  const path = useLocation({ select: (l) => l.pathname });
  const currentName = orgs.held?.name ?? '';
  return (
    <Select
      aria-label="Go to another organization"
      data={orgs.orgs.map((o) => ({ value: o.id, label: o.name }))}
      value={current}
      allowDeselect={false}
      {...orgs.select}
      nothingFoundMessage="No organization matches"
      variant="unstyled"
      leftSection={<OrgMark name={currentName} />}
      leftSectionWidth={40}
      rightSection={<IconSelector {...ICON_INLINE} style={{ color: 'var(--mantine-color-dimmed)' }} />}
      styles={{ input: { fontWeight: 600, paddingLeft: 40 }, section: { paddingLeft: 8 } }}
      className="org-switcher"
      comboboxProps={{ withinPortal: true, position: 'top-start' }}
      onChange={(v) => { if (v) void navigate({ to: staffPageOf(path) ? '/overview' : path, search: { org: v } }); }}
    />
  );
}

function AccountMenu({ name, role, authenticator, changeEmail, search }: { name: string; role: string; authenticator: boolean; changeEmail: boolean; /** The organization in view, carried by every link. */ search: AppSearch }) {
  const [changing, setChanging] = useState(false);
  const [changingAuthenticator, setChangingAuthenticator] = useState(false);
  const [changingEmail, setChangingEmail] = useState(false);
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const pick = (value: string) => {
    const scheme = SCHEMES.find((s) => s.value === value);
    if (scheme) setColorScheme(scheme.value);
  };
  return (
    <>
    <Menu position="bottom-end" width={248} withinPortal transitionProps={menuFromAbove}>
      <Menu.Target>
        <Avatar component="button" type="button" name={name} variant="default" radius="xl" size={32} aria-label={`Account: ${name}`} style={{ cursor: 'pointer', border: 0 }} />
      </Menu.Target>
      <Menu.Dropdown>
        <Box px="sm" pt={6} pb="xs">
          <Text size="sm" fw={600} lineClamp={1}>{name}</Text>
          <Text size="xs" c="dimmed">{role}</Text>
        </Box>
        <Menu.Divider />
        <Menu.Label>Appearance</Menu.Label>
        <Menu.RadioGroup value={colorScheme} onChange={pick}>
          {SCHEMES.map((s) => (
            <Menu.RadioItem key={s.value} value={s.value} closeMenuOnClick>{s.label}</Menu.RadioItem>
          ))}
        </Menu.RadioGroup>
        <Menu.Divider />
        <Menu.Item leftSection={<IconScale {...ICON_INLINE} />} renderRoot={(p) => <Link {...p} to="/your-legal-documents" search={search} />}>Your legal documents</Menu.Item>
        <Menu.Item leftSection={<IconKey {...ICON_INLINE} />} onClick={() => setChanging(true)}>Change password</Menu.Item>
        {changeEmail && <Menu.Item leftSection={<IconMail {...ICON_INLINE} />} onClick={() => setChangingEmail(true)}>Change email</Menu.Item>}
        {authenticator && <Menu.Item leftSection={<IconShieldLock {...ICON_INLINE} />} onClick={() => setChangingAuthenticator(true)}>Change authenticator</Menu.Item>}
        <form method="post" action="/auth/logout" onSubmit={signingOut}>
          <Menu.Item component="button" type="submit" leftSection={<IconLogout {...ICON_INLINE} />}>Sign out</Menu.Item>
        </form>
      </Menu.Dropdown>
    </Menu>
    <ChangePasswordModal opened={changing} onClose={() => setChanging(false)} />
    <ChangeAuthenticatorModal opened={changingAuthenticator} onClose={() => setChangingAuthenticator(false)} />
    <ChangeEmailModal opened={changingEmail} onClose={() => setChangingEmail(false)} />
    </>
  );
}
